export const MAX_SOURCE_BYTES = 10 * 1024 * 1024;
export const MAX_REQUEST_PHOTO_BYTES = 4 * 1024 * 1024;
export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

const messages = {
  fr: {
    unsupported: 'Format non pris en charge. Choisissez une photo JPG, PNG ou WebP. Pour une photo HEIC/HEIF sur iPhone, exportez-la en JPEG ou utilisez une capture d’écran.',
    large: 'Cette photo dépasse 10 Mo. Exportez une version plus petite ou utilisez une capture d’écran.',
    unreadable: 'Impossible de lire cette photo. Téléchargez-la depuis iCloud si nécessaire, puis choisissez une autre version JPG ou PNG.',
    preparation: 'La préparation de la photo a échoué. Réessayez avec une version plus petite ou une capture d’écran.',
    total: 'Les photos dépassent la limite totale après optimisation (4 Mo). Retirez une photo ou choisissez des versions plus petites.',
    network: 'La connexion a été interrompue. Vérifiez votre connexion et réessayez. Gardez cette page ouverte pendant le traitement.',
    timeout: 'Le traitement prend trop de temps. Réessayez dans quelques minutes ou demandez une estimation gratuite.',
    unavailable: 'Le service est temporairement indisponible. Réessayez dans quelques minutes ou contactez Éco-Bain.',
    rate: 'La limite temporaire de générations est atteinte. Réessayez plus tard ou demandez une estimation gratuite.',
    storage: 'La photo n’a pas pu être conservée pour le formulaire. Vous pourrez l’ajouter à nouveau à votre demande.',
    preparing: 'Préparation de la photo…',
    result: 'L’aperçu reçu ne peut pas être affiché. Vérifiez votre connexion et réessayez dans quelques minutes.',
    contactUnconfirmed: 'L’envoi n’a pas pu être confirmé. Votre demande a peut-être été reçue. Vérifiez votre connexion et contactez Éco-Bain avant de la renvoyer.',
  },
  en: {
    unsupported: 'Unsupported format. Choose a JPG, PNG or WebP photo. For an iPhone HEIC/HEIF photo, export it as JPEG or use a screenshot.',
    large: 'This photo exceeds 10 MB. Export a smaller version or use a screenshot.',
    unreadable: 'Unable to read this photo. Download it from iCloud if needed, then choose another JPG or PNG version.',
    preparation: 'Photo preparation failed. Try a smaller version or a screenshot.',
    total: 'The optimized photos exceed the total limit (4 MB). Remove a photo or choose smaller versions.',
    network: 'The connection was interrupted. Check your connection and try again. Keep this page open during processing.',
    timeout: 'Processing is taking too long. Try again in a few minutes or request a free estimate.',
    unavailable: 'The service is temporarily unavailable. Try again in a few minutes or contact Éco-Bain.',
    rate: 'The temporary generation limit has been reached. Try again later or request a free estimate.',
    storage: 'The photo could not be saved for the form. You can add it again to your request.',
    preparing: 'Preparing photo…',
    result: 'The preview could not be displayed. Check your connection and try again in a few minutes.',
    contactUnconfirmed: 'Your submission could not be confirmed. Your request may have been received. Check your connection and contact Éco-Bain before sending it again.',
  },
};
export const photoMessage = (language, code) => (messages[language] || messages.fr)[code];

// Extension fallback is only for empty MIME metadata; decoding still validates the file.
export function validatePhoto(file) {
  const type = (file?.type || '').toLowerCase();
  if (!file || !file.size) return 'unreadable';
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(type) &&
      !(type === '' && /\.(jpe?g|png|webp)$/i.test(file.name))) return 'unsupported';
  if (file.size > MAX_SOURCE_BYTES) return 'large';
  return '';
}

export function preparePhoto(file) {
  const invalid = validatePhoto(file);
  if (invalid) return Promise.reject(new Error(invalid));
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    let canvas;
    let settled = false;
    const finish = (error, result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      image.onload = image.onerror = null;
      URL.revokeObjectURL(url);
      if (canvas) { canvas.width = 0; canvas.height = 0; }
      error ? reject(new Error(error)) : resolve(result);
    };
    const timer = setTimeout(() => finish('preparation'), 30000);
    image.onerror = () => finish('unreadable');
    image.onload = () => {
      try {
        if (!image.naturalWidth || !image.naturalHeight) return finish('unreadable');
        const scale = Math.min(1, 1800 / Math.max(image.naturalWidth, image.naturalHeight));
        canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) return finish('preparation');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => {
          if (!blob || blob.size > MAX_REQUEST_PHOTO_BYTES) return finish('preparation');
          try {
            finish(null, new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', {
              type: 'image/jpeg', lastModified: file.lastModified,
            }));
          } catch { finish('preparation'); }
        }, 'image/jpeg', 0.88);
      } catch { finish('preparation'); }
    };
    image.src = url;
  });
}

export function responseError(response, data, language) {
  if (response.status === 413) return photoMessage(language, 'total');
  if (response.status === 408 || response.status === 504) return photoMessage(language, 'timeout');
  if (response.status === 429) return data?.error || photoMessage(language, 'rate');
  return data?.error || photoMessage(language, 'unavailable');
}

export function checkPreviewImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const finish = (ok) => {
      clearTimeout(timer);
      image.onload = image.onerror = null;
      ok ? resolve() : reject(new Error('result'));
    };
    const timer = setTimeout(() => finish(false), 30000);
    image.onload = () => finish(image.naturalWidth > 0);
    image.onerror = () => finish(false);
    image.src = url;
  });
}
